import React, { useEffect, useRef } from "react";

// Catch-all screen for Orenda onboarding steps that don't have a dedicated
// screen yet, plus the v2 "waiting" and "terminal" states:
//
//   PROCESSING      → waiting (short); poll fast
//   IN_REVIEW       → waiting (longer; risk/compliance); poll every 30s
//   COMPLETED       → terminal success
//   DECLINED        → terminal failure
//   ERROR           → terminal error
//   ADD_UBOS        → company flow; show a note until we build the screen
//   PROVIDER_DOCS   → rare; contact-support screen
//   (unknown)       → generic processing message

const POLL_FAST_MS = 5_000;    // PROCESSING
const POLL_SLOW_MS = 30_000;   // IN_REVIEW

// Steps that should trigger a background poll of viewapplication so we auto-
// advance when the backend finishes work.
const POLL_INTERVALS = {
  PROCESSING: POLL_FAST_MS,
  IN_REVIEW: POLL_SLOW_MS,
  // v1 aliases
  RISK: POLL_SLOW_MS,
  HOLDER_CREATION: POLL_FAST_MS,
  ACCOUNT_CREATION: POLL_FAST_MS,
};

const OrendaStepPlaceholder = ({ status, onAdvance }) => {
  const currentStep = status?.currentStep;
  const intervalRef = useRef(null);

  // Background poll while the backend is finishing async work.
  useEffect(() => {
    const ms = POLL_INTERVALS[currentStep];
    if (!ms || typeof onAdvance !== "function") return undefined;
    intervalRef.current = window.setInterval(() => {
      try { onAdvance(); } catch { /* ignore and keep polling */ }
    }, ms);
    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [currentStep, onAdvance]);

  // Terminal: COMPLETED → success screen
  if (currentStep === "COMPLETED") {
    return (
      <>
        <h2 className="orenda-heading">You're all set</h2>
        <p className="orenda-subheading">
          Your application is approved and your account is ready to use.
        </p>
        <div className="orenda-card">
          <div className="orenda-eyebrow">Status</div>
          <div style={{ fontSize: 18, fontWeight: 500, color: "#2f6b3a" }}>
            Approved
          </div>
        </div>
      </>
    );
  }

  // Terminal: DECLINED → support contact screen
  if (currentStep === "DECLINED") {
    return (
      <>
        <h2 className="orenda-heading">We can't open an account at this time</h2>
        <p className="orenda-subheading">
          Your application wasn't approved. If you believe this is a mistake,
          please get in touch with our support team.
        </p>
        <div className="orenda-alert orenda-alert--warn">
          <strong>Need help?</strong>
          <div style={{ marginTop: 4 }}>
            Contact support with your reference and they'll take a look.
          </div>
        </div>
      </>
    );
  }

  // Terminal: ERROR → something went wrong
  if (currentStep === "ERROR") {
    return (
      <>
        <h2 className="orenda-heading">Something went wrong</h2>
        <p className="orenda-subheading">
          We couldn't complete your verification. Please contact support so we
          can take a look.
        </p>
        <div className="orenda-alert orenda-alert--warn">
          Our team has been notified. Reach out and we'll help you get back on
          track.
        </div>
      </>
    );
  }

  // IN_REVIEW → "we're checking everything, hang tight"
  if (currentStep === "IN_REVIEW" || currentStep === "RISK") {
    return (
      <>
        <h2 className="orenda-heading">We're reviewing your application</h2>
        <p className="orenda-subheading">
          Our team is running the final checks. This usually takes a few
          minutes, but can take longer. This page updates automatically — feel
          free to come back later.
        </p>
        <div className="orenda-card">
          <div className="orenda-eyebrow">Status</div>
          <div style={{ fontSize: 18, fontWeight: 500, color: "#254069" }}>
            In review
          </div>
        </div>
      </>
    );
  }

  // PROCESSING → short wait between steps
  if (
    currentStep === "PROCESSING" ||
    currentStep === "HOLDER_CREATION" ||
    currentStep === "ACCOUNT_CREATION"
  ) {
    return (
      <>
        <h2 className="orenda-heading">Just a moment</h2>
        <p className="orenda-subheading">
          We're finishing up — this will only take a few seconds.
        </p>
        <div className="orenda-card">
          <div className="orenda-eyebrow">Status</div>
          <div style={{ fontSize: 18, fontWeight: 500, color: "#254069" }}>
            Processing…
          </div>
        </div>
      </>
    );
  }

  // ADD_UBOS → company flow, not yet built
  if (currentStep === "ADD_UBOS") {
    return (
      <>
        <h2 className="orenda-heading">Add beneficial owners</h2>
        <p className="orenda-subheading">
          Your company's beneficial owners each need to complete their own
          verification. We'll walk you through this shortly.
        </p>
        <div className="orenda-alert orenda-alert--info">
          If you need this step today, please reach out to support so we can
          send verification links to each owner.
        </div>
      </>
    );
  }

  // PROVIDER_DOCUMENTS → needs manual setup per Orenda docs
  if (currentStep === "PROVIDER_DOCUMENTS") {
    return (
      <>
        <h2 className="orenda-heading">Additional documents required</h2>
        <p className="orenda-subheading">
          Your account provider needs you to sign a few documents. Please
          contact support to set up this signing flow.
        </p>
      </>
    );
  }

  // Unknown step → generic holding
  return (
    <>
      <h2 className="orenda-heading">Almost there</h2>
      <p className="orenda-subheading">
        We're finalising your application. This page will update automatically
        once we're done.
      </p>
      <div className="orenda-card">
        <div className="orenda-eyebrow">Current stage</div>
        <div style={{ fontSize: 18, fontWeight: 500, color: "#16181d" }}>
          Processing
        </div>
      </div>
    </>
  );
};

export default OrendaStepPlaceholder;
